// components/landing/BuilderLandingPage.tsx (the original AI-website-builder landing page, preserved; see docs/landing-modes.md)
// Main landing page — single full-viewport hero with animated WebGL metaball
// background, glassmorphism badge, gradient headline, and auth CTA buttons.

import { useState, useEffect, memo } from 'react'
import { Link } from 'react-router'

function BuilderLandingPage() {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <div className="min-h-screen text-white overflow-hidden relative">
      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-5 max-w-5xl mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <img
            src="/logo-symbol.png"
            alt="SiteGenius"
            width={36}
            height={36}
            className="rounded-xl"
          />
          <span className="font-bold text-xl tracking-tight">SiteGenius</span>
        </div>
        <Link
          to="/auth/login"
          className="font-medium text-neutral-400 hover:text-white transition-colors px-4 py-2"
          style={{ minHeight: 44 }}
        >
          Sign in
        </Link>
      </nav>

      {/* Hero — centered */}
      <main className="relative z-20 flex flex-col items-center justify-center px-6 min-h-[calc(100vh-160px)]">
        <div
          className={`text-center max-w-3xl transition-all duration-1000 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-8"
            style={{
              background: 'rgba(18, 16, 14, 0.92)',
              backdropFilter: 'blur(24px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
              border: '1px solid rgba(255, 255, 255, 0.10)',
              boxShadow:
                '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            {/* Sparkles icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
              style={{ color: '#F59E4A' }}
              aria-hidden="true"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
            </svg>
            <span>AI-powered website builder</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.1] tracking-tight mb-4">
            <span style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
              Describe it.
            </span>
            <br />
            <span className="relative inline-block">
              {/* Shadow layer behind the gradient text */}
              <span
                className="absolute inset-0 text-black/40 blur-md select-none"
                aria-hidden="true"
              >
                We build it.
              </span>
              {/* Gradient text */}
              <span
                className="relative bg-clip-text"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #F59E4A, #E8853A, #F5B74A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                We build it.
              </span>
            </span>
          </h1>

          {/* Early Access Badge */}
          <p
            className="text-lg font-medium tracking-wide mb-6"
            style={{ color: '#F5B74A' }}
          >
            Early Access Now Available
          </p>

          {/* Subheadline */}
          <p
            className="text-xl text-neutral-300 leading-relaxed mb-10 max-w-xl mx-auto"
            style={{ textShadow: '0 1px 10px rgba(0,0,0,0.4)' }}
          >
            Create beautiful, functional websites with just your words.
            No code. No design skills.
          </p>

          {/* CTA */}
          <Link
            to="/auth/signup"
            className="inline-flex items-center gap-2 rounded-xl px-10 py-5 text-lg font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: '#ffffff',
              color: '#000000',
              boxShadow: '0 25px 50px -12px rgba(255, 255, 255, 0.1)',
            }}
          >
            Get early access
            {/* ArrowRight icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        </div>
      </main>
    </div>
  )
}

export default memo(BuilderLandingPage)
